import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  Tag,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const PropertyDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 1,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // ------------------------------ DEBUG INFO
  console.log('🔍 PropertyDetails Debug:', {
    receivedFormMode: formMode,
    receivedCurrentStep: currentStep, 
    receivedLastSavedStep: lastSavedStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    dataAttributes: data?.attributes,
    dataAreaInfo: data?.area_information,
    attributeValuesCount: attributeValues.length
  });

  // ------------------------------ CHILD EDIT MODE
  const isEditMode = useMemo(() => {
    const result = formMode === "edit" || lastSavedStep >= currentStep;
    return result;
  }, [formMode, lastSavedStep, currentStep]);

  // ------------------------------ STATE
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Form values
  const [form, setForm] = useState({
    propertyType: "",
    buildingType: "",
    propertyAge: "",
    floor: "",
    totalFloors: "",
    superBuiltUpArea: "",
    builtUpAreaUnit: "",
    furnishing: "",
    otherFeatures: [],
  });

  const FIELD_TO_ATTRIBUTE_MAP = useMemo(
    () => ({
      propertyType: "com_rent_property_type", 
      buildingType: "com_rent_building_type",
      propertyAge: "com_rent_age_of_property", 
      floor: "com_rent_floor",
      totalFloors: "com_rent_total_floor",
      superBuiltUpArea: "com_sale_super_built_up_area",
      builtUpAreaUnit: "built_up_area_unit",
      furnishing: "com_rent_furnishing",
      otherFeatures: "com_rent_other_features",
    }),
    []
  );

  // ------------------------------ VALIDATION RULES
  const validationRules = useMemo(
    () => ({
      propertyType: {
        required: true,
        message: "Property Type is required"
      },
      buildingType: {
        required: true, 
        message: "Building Type is required"
      },
      propertyAge: {
        required: true,
        message: "Property Age is required"
      },
      floor: {
        required: true,
        message: "Floor is required"
      },
      totalFloors: {
        required: true,
        message: "Total Floors is required"
      },
      superBuiltUpArea: {
        required: true,
        message: "Super Built-Up Area is required",
        custom: (v) => {
          if (v && Number(v) <= 0) return "Super Built-Up Area must be greater than 0";
          return null;
        }
      },
      builtUpAreaUnit: {
        required: true,
        message: "Area unit is required"
      },
      furnishing: {
        required: true,
        message: "Furnishing is required"
      },
    }),
    []
  );

  // ------------------------------ UTILITY FUNCTIONS
  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );
const getOptions = useCallback(
  (fieldKey) => {
    const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);

    let options = attributeValues.filter(
      (it) => it?.isActive && normalize(it.attrName) === match
    );

    // 🏠 Custom Sorting for BHK / RK
    if (fieldKey === "bhkType") {
      options = [...options].sort((a, b) => {
        const parse = (label) => {
          const numMatch = label?.match(/\d+/);
          const number = numMatch ? Number(numMatch[0]) : 999;
          const isRK = label?.toLowerCase().includes("rk");
          const typePriority = isRK ? 0 : 1; // RK first
          return { number, typePriority };
        };

        const A = parse(a.label);
        const B = parse(b.label);

        if (A.number !== B.number) {
          return A.number - B.number;
        }

        return A.typePriority - B.typePriority;
      });
    }

   if (fieldKey === "propertyAge") {
  const getPriority = (label = "") => {
    const lower = label.toLowerCase().trim();

    if (lower.startsWith("less")) return 1;
    if (lower.startsWith("1")) return 2;
    if (lower.startsWith("3")) return 3;
    if (lower.startsWith("5")) return 4;
    if (lower.startsWith("more")) return 5;

    return 999;
  };

  options = [...options].sort(
    (a, b) => getPriority(a.label) - getPriority(b.label)
  );
}
    return options;
  },
  [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
);

  const findOptionIdByValue = useCallback((fieldKey, backendValue) => {
    if (!backendValue && backendValue !== 0) return "";
    const options = getOptions(fieldKey);
    
    console.log(`🔍 Finding option for ${fieldKey}:`, {
      backendValue,
      options: options.map(opt => ({ id: opt.id, label: opt.label }))
    });
    
    const foundById = options.find(opt => 
      String(opt.id) === String(backendValue)
    );
    if (foundById) {
      console.log(`✅ Found ${fieldKey} by ID:`, foundById);
      return foundById.id;
    }

    const foundByLabel = options.find(opt => 
      normalize(opt.label) === normalize(backendValue)
    );
    if (foundByLabel) {
      console.log(`✅ Found ${fieldKey} by label:`, foundByLabel);
      return foundByLabel.id;
    }

    console.log(`❌ Could not find ${fieldKey} for value:`, backendValue);
    return "";
  }, [getOptions, normalize]);

  // ------------------------------ VALIDATION
  const getFieldError = useCallback(
    (field) => {
      const rule = validationRules[field];
      if (!rule) return null;

      const val = form[field];

      if (rule.required && (!val || val === "")) {
        return rule.message;
      }

      if (rule.custom) {
        return rule.custom(val);
      }

      return null;
    },
    [form, validationRules]
  );

  const handleBlur = useCallback((field) => setTouched((prev) => ({ ...prev, [field]: true })), []);

  // ------------------------------ FORM FIELD UPDATES
  const updateField = useCallback((key, value) => {
    console.log(`🔄 Updating field ${key}:`, value);
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const toggleFeature = useCallback((featureId) => {
    setForm((prev) => {
      const exists = prev.otherFeatures.includes(featureId);
      const next = exists
        ? prev.otherFeatures.filter((f) => f !== featureId)
        : [...prev.otherFeatures, featureId];
      console.log(`🔄 Toggled feature ${featureId}:`, { exists, next });
      return { ...prev, otherFeatures: next };
    });
  }, []);


// ------------------------------ INITIALIZE FORM WITH EXISTING DATA
useEffect(() => {
  if (!data || !attributeValues.length || isInitialized) return;

  console.log("💧 Hydrating form with existing data:", {
    attributes: data.attributes,
    areaInfo: data.area_information,
    fullData: data,
  });

  const attributes = data.attributes || {};
  const areaInfo = data.area_information || {};

  const updatedForm = {
    propertyType:
      findOptionIdByValue(
        "propertyType",
        attributes.com_rent_property_type
      ) || "",

    buildingType:
      findOptionIdByValue(
        "buildingType",
        attributes.com_rent_building_type
      ) || "",

    propertyAge:
      findOptionIdByValue(
        "propertyAge",
        attributes.com_rent_age_of_property
      ) || "",

    floor:
      findOptionIdByValue(
        "floor",
        attributes.com_rent_floor
      ) || "",

    totalFloors:
      findOptionIdByValue(
        "totalFloors",
        attributes.com_rent_total_floor
      ) || "",

    superBuiltUpArea:
      areaInfo.com_sale_super_built_up_area || "",

    builtUpAreaUnit:
      findOptionIdByValue(
        "builtUpAreaUnit",
        areaInfo.built_up_area_unit
      ) || "",

    furnishing:
      findOptionIdByValue(
        "furnishing",
        attributes.com_rent_furnishing
      ) || "",

    otherFeatures: [],
  };

  console.log("💧 Form values after mapping:", updatedForm);

  // ============================================
  // OTHER FEATURES
  // ============================================

  const otherFeaturesData =
    attributes.com_rent_other_features;

  console.log(
    "🔍 Backend Other Features:",
    otherFeaturesData
  );

  if (
    otherFeaturesData !== undefined &&
    otherFeaturesData !== null &&
    otherFeaturesData !== ""
  ) {
    const otherFeaturesOptions =
      getOptions("otherFeatures");

    let featureValues = [];

    // Backend returned an array
    if (Array.isArray(otherFeaturesData)) {
      featureValues = otherFeaturesData;
    }

    // Backend returned comma-separated IDs
    else if (typeof otherFeaturesData === "string") {
      featureValues = otherFeaturesData
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
    }

// Backend returned a single number
else if (typeof otherFeaturesData === "number") {
  featureValues = [otherFeaturesData];
}

// Backend returned boolean
else if (typeof otherFeaturesData === "boolean") {
  if (otherFeaturesData) {
    featureValues =
      otherFeaturesOptions.map(
        (option) => option.id
      );
  }
}

    // Convert backend values to option IDs
    updatedForm.otherFeatures = featureValues
      .map((value) => {
        // First try matching by ID
        const foundById =
          otherFeaturesOptions.find(
            (option) =>
              String(option.id) === String(value)
          );

        if (foundById) {
          return foundById.id;
        }

        // If ID doesn't match, try matching by label
        const foundByLabel =
          otherFeaturesOptions.find(
            (option) =>
              normalize(option.label) ===
              normalize(value)
          );

        return foundByLabel
          ? foundByLabel.id
          : null;
      })
      .filter(Boolean);
  }

  console.log(
    "💧 Final form state after hydration:",
    updatedForm
  );

  setForm(updatedForm);
  setIsInitialized(true);
}, [
  data,
  attributeValues,
  isInitialized,
  getOptions,
  findOptionIdByValue,
  normalize,
]);

  // ------------------------------ SAVE HANDLER
  const handleSave = useCallback(
    async (navigateNext = false) => {
      console.log('💾 Starting save process...', {
        navigateNext,
        propertyUuid,
        currentStep,
        lastSavedStep,
        isEditMode,
        formData: form,
        shouldCreateNew: !propertyUuid
      });

      // Mark all fields as touched for validation
      const allTouched = Object.keys(form).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );
      setTouched(allTouched);

      // Check for validation errors
      const hasValidationErrors = Object.keys(validationRules).some(
        (key) => !!getFieldError(key)
      );
      
      if (hasValidationErrors) {
        setError("Please fill all required fields correctly");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      try {
        let uuid = propertyUuid;

        // ONLY create property if no UUID exists AND we're in add mode
        if (!uuid && formMode === "add") {
          console.log('🆕 Creating new property...');
          uuid = await createProperty(form);
          console.log('✅ Property created with UUID:', uuid);
          onSaveSuccess?.(uuid, form, currentStep);
        } else if (uuid) {
          console.log('📝 Updating existing property:', uuid);
          onSaveSuccess?.(uuid, form, currentStep);   // ← ADD THIS LINE
        } else {
          throw new Error("No property UUID available for saving");
        }

        // Prepare attributes payload - send numeric IDs to backend
        const attributes = {};
        Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([formKey, apiKey]) => {
          const value = form[formKey];
          
if (formKey === "otherFeatures") {
  const options = getOptions("otherFeatures");
  attributes[apiKey] = Array.isArray(value)
    ? value
        .map((id) => {
          const opt = options.find(
            (o) => String(o.id) === String(id)
          );
          return opt ? opt.label : null;
        })
        .filter(Boolean)
    : [];
} else if (value !== "" && value !== null && value !== undefined) {
  attributes[apiKey] = isNaN(Number(value)) ? value : Number(value);
}
        });

        // Prepare area information - include both super built-up area and unit
        const areaInformation = {
          com_sale_super_built_up_area: Number(form.superBuiltUpArea) || 0,
          built_up_area_unit: form.builtUpAreaUnit || "",
        };

        console.log('💾 Final payload for save:', {
          uuid,
          attributes,
          areaInformation,
          formData: form
        });

        await saveSectionAttributes(uuid, attributes, { ...form, ...areaInformation }, "property_details");

        setSuccess("Property details saved successfully!");
        console.log('✅ Save completed successfully');
        onComplete?.(form);

        if (navigateNext) {
          console.log('➡️ Navigating to next step...');
          onSaved?.(uuid, form);
          onNavigateNext?.(form);
        }
      } catch (err) {
        console.error('❌ Save failed:', err);
        setError(err.message || "Failed to save property details");
      } finally {
        setSaving(false);
      }
    },
[
  form,
  propertyUuid,
  formMode,
  createProperty,
  onSaveSuccess,
  saveSectionAttributes,
  onComplete,
  onSaved,
  onNavigateNext,
  validationRules,
  getFieldError,
  FIELD_TO_ATTRIBUTE_MAP,
  getOptions,
  currentStep,
  isEditMode,
  lastSavedStep,
]
  );

  // ------------------------------ RENDER UI
  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // Show loading until form is properly initialized with existing data
  if (!isInitialized && propertyUuid && data) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p>Loading property details...</p>
        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '1rem' }}>
          UUID: {propertyUuid}<br/>
          Data received: {Object.keys(data).length > 0 ? 'Yes' : 'No'}<br/>
          Attributes: {data?.attributes ? Object.keys(data.attributes).length : 0}
        </div>
      </div>
    );
  }

  return (
    <>


      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999, minWidth: "300px" }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999, minWidth: "300px" }}
        />
      )}

      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
              <Stack gap={6}>
                
              
                {/* ROW 1: Property Type & Building Type */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("propertyType", "Property Type", true)}
                    </Column>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("buildingType", "Building Type", true)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ROW 2: Property Age */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("propertyAge", "Property Age", true)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ROW 3: Floor Configuration */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("floor", "Floor", true)}
                    </Column>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("totalFloors", "Total Floors", true)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ROW 4: Area Information */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                        <div style={{ flex: 2 }}>
                          {renderNumberField("superBuiltUpArea", "Super Built-up Area" ,"0", true)}
                        </div>
                        <div style={{ flex: 1 }}>
                          {renderDropdown("builtUpAreaUnit", "Unit", true)}
                        </div>
                      </div>
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ROW 5: Furnishing */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("furnishing", "Furnishing", true)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ROW 6: Additional Features */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={16}>
                      <FormGroup legendText="Other Features">
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", padding: "0.25rem 0" }}>
                          {getOptions("otherFeatures").map((feature) => {
                            const selected = form.otherFeatures.includes(feature.id);
                            return (
                              <Tag
                                key={feature.id}
                                type={selected ? "blue" : "gray"}
                                onClick={() => toggleFeature(feature.id)}
                                style={{
                                  cursor: "pointer",
                                  padding: "0.5rem 1rem",
                                  fontSize: "0.875rem",
                                  border: selected ? "2px solid #0f62fe" : "2px solid transparent",
                                }}
                                title={feature.label}
                              >
                                {feature.label}
                              </Tag>
                            );
                          })}
                        </div>
                      </FormGroup>
                    </Column>
                  </Grid>
                </FormGroup>

                {/* BUTTONS */}
                {CommonButtons && (
                  <CommonButtons
                    onSave={() => handleSave(false)}   // SAVE ONLY
                    onNext={() => handleSave(true)}    // NEXT = SAVE + NAVIGATE
                    showSave
                    showNext
                    isSaveLoading={saving}
                    onBack={onBack}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default PropertyDetails;